
export function Proxify<T extends object = any, Y = T>({
  get,
  set,
  apply,
}: {
  get?: <X extends keyof T>(p: {
    prop: X;
    original: (prop: X) => T[X];
  }) => any;
  set?: <X extends keyof T>(p: {
    prop: X;
    value: T[X];
    original: (prop: X, value: T[X]) => boolean;
  }) => boolean;
  apply?: (p: {
    args: any[];
    original: (...args: any[]) => any;
  }) => any;
}, obj?: T) {
  const defaultTarget = apply ? (() => {}) : {};
  return new Proxy(
    obj ?? defaultTarget,
    {
      get(target: any, prop: any, receiver: any): any {
        function original(prop: any): T[typeof prop] | Y[typeof prop] {
          return Proxify.getPropFromObject(target, prop, receiver);
        }

        if (get) {
          const result = get({
            prop,
            original,
          });

          if (typeof result === 'function') {
            return (...args: any[]) => result(...args);
          }

          return result;
        }

        return original(prop);
      },

      set(target: any, prop: any, value: any, receiver: any): boolean {
        function original(prop: keyof T, value: T[typeof prop]): boolean {
          return Reflect.set(target, prop, value, receiver);
        }

        // Custom set logic
        if (set) {
          return set({
            prop,
            value,
            original,
          });
        }

        // Default set behavior
        return original(prop, value);
      },

      apply(target: any, thisArg: any, argArray: any[]): any {
        function original(...args: any[]) {
          return Reflect.apply(target, thisArg, args);
        }

        if (apply) {
          return apply({
            args: argArray,
            original,
          });
        }

        return original(...argArray);
      },

      deleteProperty(target: any, prop: any): boolean {
        return Reflect.deleteProperty(target, prop);
      },

      has(target: any, prop: any): boolean {
        return Reflect.has(target, prop);
      },

      ownKeys(target: any): Array<string | symbol> {
        return Reflect.ownKeys(target);
      },
    },
  ) as (T & Y);
}

Proxify.getPropFromObject = function getPropFromObject(target: any, prop: any, receiver?: any) {
  const value = Reflect.get(target, prop, receiver);

  if (typeof value === 'function') {
    // Ensure that functions are callable and bound to the original target.
    return value.bind(target);
  }

  return value;
};
